import React, { createContext, useContext, useMemo } from "react";
import { UserContext } from "../utils/userContext";
import { getLocale, translate } from "../utils/locales";
import { setDayjsLocale } from "../utils/dayjsLocale";

const TranslationContext = createContext(null);

/**
 * Provides t(key, replacements?) using the current user's UI language.
 * Must be used inside UserProvider.
 * Sets dayjs locale synchronously so date formatting uses the UI language before any child renders.
 */
export function TranslationProvider({ children }) {
  const { user } = useContext(UserContext);
  const language = user?.languages?.ui || "English";
  setDayjsLocale(language);
  const locale = useMemo(() => getLocale(language), [language]);

  const t = useMemo(
    () => (key, replacements) => translate(locale, key, replacements),
    [locale]
  );

  const value = useMemo(() => ({ t, language }), [t, language]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * @returns {{ t: (key: string, replacements?: Record<string, string|number>) => string, language: string }}
 */
export function useT() {
  const ctx = useContext(TranslationContext);
  if (!ctx) {
    throw new Error("useT must be used within TranslationProvider");
  }
  return ctx;
}
